"use client";

import React, { useRef, useState } from "react";
import Image from "next/image";
import Text from "@components/atoms/Text";
import { Input } from "@components/atoms/Input";
import styles from "./CitySelectionDesktopHeader.module.scss";
import assetLoader from "@utils/internalImageLoder";
import { getCurrentPosition } from "@services/geolocationService";
import { CITY_ALIASES } from "@constants/cities";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";
import { reverseGeocodeCity } from "@services/geocodingService";

interface CitySelectionDesktopHeaderProps {
  search: string;
  setSearch: (value: string) => void;
  closeModal: () => void;
  handleSelect: any;
  cities: any;
}

const CitySelectionDesktopHeader = ({
  search,
  setSearch,
  closeModal,
  handleSelect,
  cities,
}: CitySelectionDesktopHeaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState("");

  const handleBackClick = () => {
    setSearch("");
    setSearchFocused(false);
    inputRef.current?.blur();
  };

  const handleUseCurrentLocation = async () => {
    setIsLocating(true);
    setLocationError("");

    if (!navigator.geolocation) {
      setLocationError("Location services are not supported on this device.");
      setIsLocating(false);
      return;
    }

    if (!cities || cities.length === 0) {
      setLocationError("Please wait for cities to load before using location.");
      setIsLocating(false);
      return;
    }

    const currentSelectedCity = getSelectedCityClient();
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_current_location",
        city_name: currentSelectedCity?.name || "",
      },
    });

    try {
      const { lat, lon } = await getCurrentPosition();
      const cityName = await reverseGeocodeCity(lat, lon);

      if (!cityName) {
        throw new Error(
          "Could not determine your city from the location. Please select manually."
        );
      }

      const normalizedCity = cityName.toLowerCase();
      let match = cities.find(
        (c: any) => c.name?.toLowerCase() === normalizedCity
      );
      if (!match && CITY_ALIASES[normalizedCity]) {
        const alias = CITY_ALIASES[normalizedCity];
        match = cities.find((c: any) => c.name?.toLowerCase() === alias);
      }
      if (match) {
        await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
          additionalProps: {
            action_type: "tap_btn",
            action_name: "tap_btn_location_fetched",
            selected_city: match.name,
            city_name: match.name || "",
          },
        });
        handleSelect(match);
      } else {
        setLocationError(
          `"${cityName}" is not available in our service area. Please select your city manually.`
        );
      }
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : "Could not get your location.";
      setLocationError(errorMessage);
    } finally {
      setIsLocating(false);
    }
  };

  return (
    <div className={styles.stickyHeader}>
      <div className={styles.HeadingWrapper}>
        <div>
          <Text className={styles.modalTitle}>Select your city</Text>
          <Text className={styles.subHeading}>
            ACKO Drive is available in{" "}
            <span className={styles.subHeadingCity}>1000+ cities</span>
          </Text>
        </div>
        <Image
          src={`${assetLoader()}/icons/Close_cross.svg`}
          alt="Close"
          width={24}
          height={24}
          loading="lazy"
          onClick={closeModal}
          className={styles.modalbackIcon}
        />
      </div>

      <div className={styles.searchContainer}>
        {searchFocused ? (
          <Image
            src={`${assetLoader()}/icons/CitySelection/Chevron_left_dark.svg`}
            alt="Back"
            className={styles.backIcon}
            width={24}
            height={24}
            loading="lazy"
            onClick={handleBackClick}
          />
        ) : (
          <Image
            src={`${assetLoader()}/icons/CitySelection/search-icon.svg`}
            alt="Search"
            className={styles.searchIcon}
            width={20}
            height={20}
            loading="lazy"
            onClick={async () => {
              inputRef.current?.focus();
              const currentSelectedCity = getSelectedCityClient();
              await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
                additionalProps: {
                  action_type: "tap_btn",
                  action_name: "tap_btn_city_search",
                  city_name: currentSelectedCity?.name || "",
                },
              });
            }}
          />
        )}

        <Input
          ref={inputRef}
          type="text"
          placeholder="Search city"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={async () => {
            setSearchFocused(true);
            const currentSelectedCity = getSelectedCityClient();
            await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
              additionalProps: {
                action_type: "tap_btn",
                action_name: "tap_btn_city_search",
                city_name: currentSelectedCity?.name || "",
              },
            });
          }}
          onBlur={(e) => {
            if (!e.target.value.trim()) {
              setSearchFocused(false);
            }
          }}
          className={styles.searchInput}
          autoFocus={false}
        />
      </div>

      <div className={styles.useLocationWrapper}>
        <button
          className={styles.useLocationButton}
          onClick={handleUseCurrentLocation}
          disabled={isLocating}
        >
          <Image
            src={`${assetLoader()}/icons/CitySelection/location.svg`}
            width={20}
            height={20}
            alt="location"
          />
          {isLocating ? "Detecting..." : "Use current location"}
        </button>
        {locationError && (
          <div className={styles.locationError}>{locationError}</div>
        )}
      </div>
    </div>
  );
};

export default CitySelectionDesktopHeader;
